import * as React from "react";
import { Link } from "gatsby";

import { cn } from "../../lib/cn";

type ButtonVariant = "primary" | "secondary" | "ghost";

type BaseProps = {
  variant?: ButtonVariant;
  className?: string;
  children: React.ReactNode;
};

type ButtonAsButtonProps = BaseProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined;
  };

type ButtonAsAnchorProps = BaseProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    href: string;
  };

type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;

function isInternalHref(href: string) {
  return href.startsWith("/") && !href.startsWith("//");
}

function mergeRelAttribute(
  rel: string | undefined,
  target: string | undefined,
) {
  if (target !== "_blank") {
    return rel;
  }

  const relValues = new Set((rel || "").split(/\s+/).filter(Boolean));
  relValues.add("noopener");
  relValues.add("noreferrer");

  return Array.from(relValues).join(" ");
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-primary text-text-strong shadow-strong hover:bg-primary-hover",
  secondary:
    "border-border-strong bg-surface-2 text-text-strong hover:border-primary",
  ghost:
    "border-border-soft bg-transparent text-text-base hover:border-primary hover:text-text-strong",
};

const baseClasses =
  "inline-flex min-h-[3.25rem] cursor-pointer items-center justify-center rounded-brand-sm border border-transparent px-[1.35rem] py-[0.85rem] font-display text-[0.95rem] font-bold uppercase tracking-[0.08em] transition-[transform,border-color,background-color,color,box-shadow] duration-150 ease-in-out hover:-translate-y-px";

export const Button = React.forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  ButtonProps
>(function Button(props, ref) {
  if ("href" in props && props.href) {
    const {
      variant = "primary",
      className,
      children,
      href,
      ...anchorProps
    } = props;
    const rel = mergeRelAttribute(anchorProps.rel, anchorProps.target);
    const classes = cn(baseClasses, variantClasses[variant], className);
    const shouldUseClientNavigation =
      isInternalHref(href) &&
      !anchorProps.target &&
      !anchorProps.download &&
      !rel?.includes("external");

    if (shouldUseClientNavigation) {
      return (
        <Link className={classes} to={href} {...anchorProps}>
          {children}
        </Link>
      );
    }

    return (
      <a
        className={classes}
        href={href}
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...anchorProps}
        rel={rel}
      >
        {children}
      </a>
    );
  }

  const buttonOnlyProps = props as ButtonAsButtonProps;
  const {
    variant = "primary",
    className,
    children,
    type = "button",
    ...buttonProps
  } = buttonOnlyProps;
  const classes = cn(baseClasses, variantClasses[variant], className);

  return (
    <button
      className={classes}
      ref={ref as React.Ref<HTMLButtonElement>}
      type={type}
      {...buttonProps}
    >
      {children}
    </button>
  );
});
